import { useEffect, useRef } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { applyUsercentricsPrimaryColor } from '@worldcoo/ui/lib/usercentrics';

interface AppliedTheme {
	primary: string;
	onPrimary: string;
	teardown?: () => void;
}

/**
 * Repaints the cookie banner in this portal's colour. Reads `--primary` off its own node, not `:root`:
 * `DivWithPrimaryColorVariable` sets it inline on a nested div, so only a descendant inherits it.
 */
export function UsercentricsPrimaryTheme() {
	const { isInIframe } = useRouteContext({ from: '__root__' });
	const probe = useRef<HTMLSpanElement>(null);
	const applied = useRef<AppliedTheme | undefined>(undefined);

	// No dependency list: an ancestor's inline style can change underneath (the NGO area's live preview).
	useEffect(() => {
		// An embed never loads the CMP (`__root.tsx`); skips a `document.body` observer on the donate page.
		if (isInIframe) return;
		const node = probe.current;
		if (!node) return;
		const styles = getComputedStyle(node);
		const primary = styles.getPropertyValue('--primary').trim();
		const onPrimary = styles.getPropertyValue('--primary-foreground').trim();
		if (applied.current?.primary === primary && applied.current.onPrimary === onPrimary) return;

		applied.current?.teardown?.();
		applied.current = { primary, onPrimary, teardown: applyUsercentricsPrimaryColor(primary, onPrimary) };
	});

	// The observers outlive a render. Clearing the ref is required: a stale entry reads "already applied", so
	// StrictMode's remount would never reinstall them.
	useEffect(
		() => () => {
			applied.current?.teardown?.();
			applied.current = undefined;
		},
		[],
	);

	return <span ref={probe} hidden />;
}
